import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Calendar as CalIcon, Video, MapPin, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TOPICS, findTopic } from "@/lib/topics";
import { createBooking, fetchTakenSlots } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

const STEPS = ["Topic", "Situation", "Your info", "Date & mode", "Done"];

const TIME_SLOTS = [
  "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00",
];

export default function Booking() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rebookId = searchParams.get("rebook");
  const [step, setStep] = useState(topicId ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [isRebook, setIsRebook] = useState(false);

  const [form, setForm] = useState({
    topic_id: topicId || "",
    custom_topic: "",
    situation: "",
    household: "",
    employment: "",
    living: "",
    in_school: "",
    dependents: "",
    debt_level: "",
    confidence: "",
    timeline: "",
    full_name: "",
    email: "",
    phone: "",
    meeting_mode: "virtual",
    preferred_date: null,
    preferred_time: "",
  });

  // Rebook flow: fetch the original booking and pre-fill everything except date/time
  useEffect(() => {
    if (!rebookId) return;
    fetchPublicBooking(rebookId)
      .then((b) => {
        const matched = TOPICS.find((t) => t.title === b.topic);
        setForm((f) => ({
          ...f,
          topic_id: matched ? matched.id : (b.custom_topic ? "other" : ""),
          custom_topic: b.custom_topic || "",
          situation: b.situation || "",
          household: b.household || "",
          employment: b.employment || "",
          living: b.living || "",
          in_school: b.in_school || "",
          full_name: b.full_name || "",
          email: b.email || "",
          phone: b.phone || "",
        }));
        setIsRebook(true);
        // Jump straight to the date/mode step — they just need to pick a new slot
        setStep(3);
        toast.success("Welcome back. Your details are saved — just pick a new time below.");
      })
      .catch(() => {
        toast.error("Sorry, we couldn't find that booking. Please start again.");
      });
  }, [rebookId]);

  const selectedTopic = useMemo(() => findTopic(form.topic_id), [form.topic_id]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [takenSlots, setTakenSlots] = useState([]);
  useEffect(() => {
    if (step !== 3 || !form.preferred_date) return;
    const dateStr = form.preferred_date.toISOString().slice(0, 10);
    let alive = true;
    fetchTakenSlots(dateStr)
      .then((slots) => { if (alive) setTakenSlots(slots); })
      .catch(() => { if (alive) setTakenSlots([]); });
    return () => { alive = false; };
  }, [form.preferred_date, step]);

  // If the user picks a date and the previously-chosen time is now taken, clear it
  useEffect(() => {
    if (form.preferred_time && takenSlots.includes(form.preferred_time)) {
      setForm((f) => ({ ...f, preferred_time: "" }));
    }
  }, [takenSlots]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const canNext = () => {
    if (step === 0) return !!form.topic_id;
    if (step === 1) {
      if (form.topic_id === "other" && !form.custom_topic.trim()) return false;
      return form.situation.trim().length > 5;
    }
    if (step === 2) return form.full_name.trim() && /.+@.+\..+/.test(form.email);
    if (step === 3) return form.preferred_date && form.preferred_time && form.meeting_mode;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const topicLabel = selectedTopic?.title || "Custom goal";
      const payload = {
        topic: topicLabel,
        custom_topic: form.topic_id === "other" ? form.custom_topic.trim() : null,
        situation: form.situation.trim(),
        household: form.household || null,
        employment: form.employment || null,
        living: form.living || null,
        in_school: form.in_school || null,
        dependents: form.dependents || null,
        debt_level: form.debt_level || null,
        confidence: form.confidence || null,
        timeline: form.timeline || null,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        meeting_mode: form.meeting_mode,
        preferred_date: form.preferred_date.toISOString().slice(0, 10),
        preferred_time: form.preferred_time,
      };
      const res = await createBooking(payload);
      setConfirmation(res);
      setStep(4);
      toast.success("Your consultation is booked. A confirmation email is on its way.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("That time was just booked. Please pick another slot.");
        // refresh taken slots so user sees the conflict
        try {
          const dateStr = form.preferred_date.toISOString().slice(0, 10);
          const slots = await fetchTakenSlots(dateStr);
          setTakenSlots(slots);
        } catch (slotErr) {
          console.warn("Failed to refresh taken slots after conflict:", slotErr);
        }
      } else {
        toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24">
        {/* Stepper */}
        <div className="mb-10" data-testid="booking-stepper">
          <div className="flex items-center gap-3">
            {STEPS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-[2px] ${i <= step ? "bg-gold" : "bg-cognac/20"} transition-colors`} />
                <p className={`mt-2 text-[10px] tracking-[0.22em] uppercase ${i === step ? "text-oxblood" : "text-inkMute"}`}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* STEP 0 — Topic */}
        {step === 0 && (
          <section className="animate-fade-up" data-testid="step-topic">
            <span className="label-overline">Step 01</span>
            <h2 className="font-serif text-4xl md:text-5xl text-oxblood mt-3">What brings you here?</h2>
            <p className="text-inkSoft mt-3">Pick whichever feels closest. You can change it later.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-10">
              {TOPICS.map((t) => {
                const Icon = t.icon;
                const active = form.topic_id === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => update("topic_id", t.id)}
                    className={`card-soft text-left p-6 transition-all ${active ? "border-gold bg-cream" : ""}`}
                    data-testid={`select-topic-${t.id}`}
                  >
                    <Icon size={22} strokeWidth={1.5} className="text-cognac" />
                    <h3 className="font-serif text-xl text-oxblood mt-4 leading-tight">{t.title}</h3>
                    <p className="text-sm text-inkSoft/90 mt-2">{t.blurb}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* STEP 1 — Situation */}
        {step === 1 && (
          <section className="animate-fade-up" data-testid="step-situation">
            <span className="label-overline">Step 02</span>
            <h2 className="font-serif text-4xl md:text-5xl text-oxblood mt-3">Tell me about your situation.</h2>
            <p className="text-inkSoft mt-3">A few brushstrokes — just enough to get started.</p>

            {form.topic_id === "other" && (
              <div className="mt-10">
                <label className="label-overline">Your goal (in your own words)</label>
                <input
                  type="text"
                  value={form.custom_topic}
                  onChange={(e) => update("custom_topic", e.target.value)}
                  className="input-flat mt-2"
                  placeholder="e.g. I want to stop being scared of my bank account"
                  data-testid="input-custom-topic"
                />
              </div>
            )}

            <div className="mt-10">
              <label className="label-overline">Briefly describe what's going on</label>
              <textarea
                rows={5}
                value={form.situation}
                onChange={(e) => update("situation", e.target.value)}
                className="input-flat mt-2 resize-none"
                placeholder="What does money look like for you right now? What would change if we worked together?"
                data-testid="input-situation"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mt-12">
              <ChoiceRow label="Household" value={form.household} onChange={(v) => update("household", v)} options={["Just me", "Couple", "Family with kids", "Living with parents", "Single parent"]} testid="household" />
              <ChoiceRow label="Employment" value={form.employment} onChange={(v) => update("employment", v)} options={["Employed", "Self-employed", "Student", "Between jobs", "Retired", "Stay-at-home parent"]} testid="employment" />
              <ChoiceRow label="Living" value={form.living} onChange={(v) => update("living", v)} options={["Renting", "Own home", "With family", "Roommates", "Other"]} testid="living" />
              <ChoiceRow label="In school?" value={form.in_school} onChange={(v) => update("in_school", v)} options={["Yes", "No", "Considering it"]} testid="in-school" />
              <ChoiceRow label="Dependents" value={form.dependents} onChange={(v) => update("dependents", v)} options={["None", "Children", "Aging parents", "Both"]} testid="dependents" />
              <ChoiceRow label="Debt level" value={form.debt_level} onChange={(v) => update("debt_level", v)} options={["None", "Manageable", "Heavy", "Overwhelming"]} testid="debt" />
              <ChoiceRow label="How confident are you with money?" value={form.confidence} onChange={(v) => update("confidence", v)} options={["Just starting", "Some basics", "Pretty confident", "Already advanced"]} testid="confidence" />
              <ChoiceRow label="Timeline to make progress" value={form.timeline} onChange={(v) => update("timeline", v)} options={["Right away", "Within 3 months", "Within a year", "Just exploring"]} testid="timeline" />
            </div>
          </section>
        )}

        {/* STEP 2 — Personal info */}
        {step === 2 && (
          <section className="animate-fade-up" data-testid="step-info">
            <span className="label-overline">Step 03</span>
            <h2 className="font-serif text-4xl md:text-5xl text-oxblood mt-3">A few details about you.</h2>
            <p className="text-inkSoft mt-3">Kept strictly confidential. Never shared.</p>

            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mt-12">
              <div>
                <label className="label-overline">Full name</label>
                <input type="text" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="input-flat mt-2" data-testid="input-name" />
              </div>
              <div>
                <label className="label-overline">Email</label>
                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input-flat mt-2" placeholder="you@example.com" data-testid="input-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-overline">Phone (optional)</label>
                <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input-flat mt-2" data-testid="input-phone" />
              </div>
            </div>
          </section>
        )}

        {/* STEP 3 — Date & mode */}
        {step === 3 && (
          <section className="animate-fade-up" data-testid="step-schedule">
            <span className="label-overline">Step 04</span>
            <h2 className="font-serif text-4xl md:text-5xl text-oxblood mt-3">Pick a time that suits you.</h2>
            <p className="text-inkSoft mt-3">A calendar invite will be emailed to you — no need to write it down.</p>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-10 mt-12">
              <div>
                <label className="label-overline">Choose a mode</label>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button type="button" onClick={() => update("meeting_mode", "virtual")} className={`card-soft p-5 text-left ${form.meeting_mode === "virtual" ? "border-gold bg-cream" : ""}`} data-testid="mode-virtual">
                    <Video size={20} strokeWidth={1.5} className="text-cognac" />
                    <p className="font-serif text-lg text-oxblood mt-3">Virtual</p>
                    <p className="text-xs text-inkMute mt-1">Video call link sent before</p>
                  </button>
                  <button type="button" onClick={() => update("meeting_mode", "in_person")} className={`card-soft p-5 text-left ${form.meeting_mode === "in_person" ? "border-gold bg-cream" : ""}`} data-testid="mode-in-person">
                    <MapPin size={20} strokeWidth={1.5} className="text-cognac" />
                    <p className="font-serif text-lg text-oxblood mt-3">In person</p>
                    <p className="text-xs text-inkMute mt-1">Address shared after booking</p>
                  </button>
                </div>

                <label className="label-overline block mt-10">Time</label>
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {TIME_SLOTS.map((t) => {
                    const taken = takenSlots.includes(t);
                    const active = form.preferred_time === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={taken}
                        onClick={() => !taken && update("preferred_time", t)}
                        title={taken ? "Already booked" : undefined}
                        className={`border text-sm py-2.5 transition-colors ${
                          taken
                            ? "border-cognac/15 text-inkMute/50 line-through cursor-not-allowed bg-cream/30 pointer-events-none"
                            : active
                            ? "border-gold bg-gold/10 text-oxblood"
                            : "border-cognac/25 text-inkSoft hover:border-cognac"
                        }`}
                        data-testid={`time-${t}`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
                {form.preferred_date && takenSlots.length > 0 && (
                  <p className="mt-3 text-xs text-inkMute" data-testid="taken-slots-note">
                    Crossed-out slots are already booked on this day.
                  </p>
                )}
              </div>

              <div>
                <label className="label-overline">Date</label>
                <div className="mt-4 border border-cognac/20 p-4 bg-cream/40">
                  <Calendar
                    mode="single"
                    selected={form.preferred_date}
                    onSelect={(d) => update("preferred_date", d)}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="booking-calendar"
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* STEP 4 — Confirmation */}
        {step === 4 && confirmation && (
          <section className="animate-fade-up text-center" data-testid="step-confirmation">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-gold/60 rounded-full text-gold">
              <Check size={26} />
            </div>
            <h2 className="font-serif text-5xl text-oxblood mt-8">You're booked.</h2>
            <p className="text-inkSoft mt-4 max-w-xl mx-auto">
              Aisha has been notified. A confirmation email is on its way to <strong>{confirmation.email}</strong> with a calendar invite (.ics) you can add to Google, Apple, or Outlook.
            </p>
            <div className="max-w-md mx-auto mt-10 border border-gold/40 bg-cream/60 p-6 text-left">
              <Row label="Topic" value={confirmation.custom_topic || confirmation.topic} />
              <Row label="When" value={`${confirmation.preferred_date} · ${confirmation.preferred_time}`} />
              <Row label="Mode" value={confirmation.meeting_mode === "virtual" ? "Virtual" : "In person"} />
              <Row label="Confirmation #" value={confirmation.id.slice(0, 8)} />
            </div>
            <div className="mt-12 flex items-center justify-center gap-4">
              <Link to="/" className="btn-ghost" data-testid="confirmation-home-button">Back to home</Link>
              <a href={`mailto:${confirmation.email}`} className="btn-primary"><Mail size={14} /> Check your inbox</a>
            </div>
          </section>
        )}

        {/* Nav buttons */}
        {step < 4 && (
          <div className="mt-16 flex items-center justify-between border-t border-cognac/15 pt-8">
            <button
              type="button"
              onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
              className="inline-flex items-center gap-2 text-sm text-cognac hover:text-oxblood link-underline"
              data-testid="booking-back-button"
            >
              <ArrowLeft size={14} /> {step === 0 ? "Back to home" : "Back"}
            </button>
            {step < 3 && (
              <button
                type="button"
                onClick={() => canNext() && setStep(step + 1)}
                disabled={!canNext()}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="booking-next-button"
              >
                Continue <ArrowRight size={14} />
              </button>
            )}
            {step === 3 && (
              <button
                type="button"
                onClick={() => canNext() && submit()}
                disabled={!canNext() || submitting}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="booking-submit-button"
              >
                <CalIcon size={14} /> {submitting ? "Booking…" : "Confirm booking"}
              </button>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function ChoiceRow({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-overline">{label}</label>
      <div className="flex flex-wrap gap-2 mt-3">
        {options.map((o) => {
          const selected = value === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(o)}
              className={`group inline-flex items-center gap-2.5 text-sm pl-3 pr-4 py-2 border transition-all ${
                selected
                  ? "border-gold bg-gold/15 text-oxblood font-medium"
                  : "border-cognac/30 text-inkSoft hover:border-cognac hover:bg-cream/40"
              }`}
              data-testid={`choice-${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span
                className={`relative w-4 h-4 rounded-full border-2 transition-all ${
                  selected ? "border-gold bg-gold" : "border-cognac/40 bg-transparent group-hover:border-cognac"
                }`}
                aria-hidden
              >
                {selected && <span className="absolute inset-[3px] rounded-full bg-oxblood" />}
              </span>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-gold/20 last:border-0">
      <span className="label-overline">{label}</span>
      <span className="text-oxblood text-sm">{value}</span>
    </div>
  );
}
